import { Box, Chip, LinearProgress, Typography } from '@mui/material'

export type GpuTelemetry = {
  gpu_sample_at?: number
  vllm_sample_at?: number
  concurrency?: number
  gpus: { index: number | null; utilization_percent: number | null; memory_used_mib: number | null; memory_total_mib: number | null; power_watts: number | null }[]
  vllm?: { running_requests?: number; waiting_requests?: number; kv_cache_fraction?: number }
}

export default function GpuMetrics({ metrics, pending, active }: { metrics?: GpuTelemetry | null; pending: number; active: number }) {
  const age = metrics?.gpu_sample_at ? Math.max(0, Math.floor(Date.now() / 1000 - metrics.gpu_sample_at)) : null
  const stale = age === null || age > 30
  const engineFresh = metrics?.vllm_sample_at && Date.now() / 1000 - metrics.vllm_sample_at <= 30
  const engine = engineFresh ? metrics?.vllm : undefined
  const low = !stale && pending > 0 && !!metrics?.gpus.length && metrics.gpus.every(g => g.utilization_percent !== null && g.utilization_percent < 40)
  return <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
    <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
      <Chip size="small" color="secondary" label="RunPod GPU" />
      <Typography variant="caption" color={stale ? 'warning.main' : 'text.secondary'}>
        {age === null ? 'Waiting for GPU telemetry' : `${stale ? 'Stale · ' : ''}sampled ${age}s ago`}
      </Typography>
      {metrics?.concurrency && <Typography variant="caption">Calls {active}/{metrics.concurrency}</Typography>}
    </Box>
    <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
      {metrics?.gpus.map((gpu, i) => <Box key={i} sx={{ minWidth: 155, flex: 1, opacity: stale ? 0.5 : 1 }}>
        <Typography variant="caption">GPU {gpu.index ?? i} · {gpu.utilization_percent === null ? 'Utilization unavailable' : `${Math.round(gpu.utilization_percent)}% busy`}</Typography>
        {gpu.utilization_percent !== null && <LinearProgress aria-label={`GPU ${i} utilization`} variant="determinate" value={Math.min(100, gpu.utilization_percent)} color={low ? 'warning' : 'secondary'} />}
        <Typography variant="caption" display="block">
          VRAM {gpu.memory_used_mib === null || !gpu.memory_total_mib ? 'unavailable' : `${(gpu.memory_used_mib / 1024).toFixed(1)} / ${(gpu.memory_total_mib / 1024).toFixed(1)} GiB`}
          {gpu.power_watts !== null ? ` · ${Math.round(gpu.power_watts)} W` : ''}
        </Typography>
      </Box>)}
      <Box>
        <Typography variant="caption" display="block">vLLM: {engine ? `${engine.running_requests ?? '—'} running · ${engine.waiting_requests ?? '—'} waiting` : 'metrics unavailable or stale'}</Typography>
        {engine?.kv_cache_fraction !== undefined && <Typography variant="caption">KV cache {Math.round(engine.kv_cache_fraction * 100)}%</Typography>}
      </Box>
    </Box>
    {low && <Typography variant="caption" color="warning.main">Low GPU activity with pending work. Check request concurrency and upstream preparation; this sample alone cannot identify the bottleneck.</Typography>}
  </Box>
}
